'use client';

import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { businessesApi } from './api';

interface Business {
  id: string;
  name: string;
  category: string;
  role: string;
  avatarUrl?: string | null;
  slug?: string | null;
}

interface BusinessContextValue {
  businesses: Business[];
  currentBusiness: Business | null;
  currentBusinessId: string | null;
  setCurrentBusinessId: (id: string) => void;
  refreshBusinesses: () => Promise<void>;
  loading: boolean;
  fetchError: string | null;
}

const BusinessContext = createContext<BusinessContextValue>({
  businesses: [],
  currentBusiness: null,
  currentBusinessId: null,
  setCurrentBusinessId: () => {},
  refreshBusinesses: async () => {},
  loading: true,
  fetchError: null,
});

export function useCurrentBusiness() {
  return useContext(BusinessContext);
}

const STORAGE_KEY = 'sweetspot_current_business_id';

export function BusinessProvider({ children }: { children: ReactNode }) {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [currentBusinessId, setCurrentBusinessIdState] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryAttemptRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) setCurrentBusinessIdState(stored);
    setHydrated(true);
  }, []);

  const fetchBusinesses = useCallback(async () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }

    const timeoutMs = 6_000;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('Business list timeout')), timeoutMs);
    });
    try {
      const data = await Promise.race([businessesApi.list(), timeoutPromise]);
      clearTimeout(timeoutId);
      if (!mountedRef.current) return;
      setFetchError(null);
      retryAttemptRef.current = 0;
      const list = Array.isArray(data) ? data : [];
      setBusinesses(list);
      const stored = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
      if (stored && list.some((b: Business) => b.id === stored)) {
        setCurrentBusinessIdState(stored);
      } else if (list.length > 0) {
        setCurrentBusinessIdState(list[0].id);
        if (typeof window !== 'undefined') localStorage.setItem(STORAGE_KEY, list[0].id);
      } else {
        if (typeof window !== 'undefined') localStorage.removeItem(STORAGE_KEY);
        setCurrentBusinessIdState(null);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (!mountedRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to load businesses';
      setFetchError(message);

      const normalized = message.toLowerCase();
      const isUnauthorized =
        normalized.includes('401') ||
        normalized.includes('unauthorized') ||
        normalized.includes('authentication is required') ||
        normalized.includes('missing authorization token') ||
        normalized.includes('invalid token');

      if (isUnauthorized) {
        const attempt = retryAttemptRef.current;
        if (attempt < 2) {
          retryAttemptRef.current = attempt + 1;
          retryTimerRef.current = setTimeout(() => void fetchBusinesses(), 2000);
          return;
        }
        retryAttemptRef.current = 0;
        setBusinesses([]);
        setCurrentBusinessIdState(null);
        if (typeof window !== 'undefined') localStorage.removeItem(STORAGE_KEY);
        return;
      }

      // Transient failures retry with bounded exponential backoff.
      const nextAttempt = retryAttemptRef.current + 1;
      retryAttemptRef.current = nextAttempt;
      const maxAttempts = 5;
      if (nextAttempt <= maxAttempts) {
        const delayMs = Math.min(5000 * 2 ** (nextAttempt - 1), 30000);
        retryTimerRef.current = setTimeout(() => {
          void fetchBusinesses();
        }, delayMs);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    retryAttemptRef.current = 0;
    void fetchBusinesses();
    return () => {
      mountedRef.current = false;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [fetchBusinesses]);

  const setCurrentBusinessId = useCallback((id: string) => {
    setCurrentBusinessIdState(id);
    if (typeof window !== 'undefined') localStorage.setItem(STORAGE_KEY, id);
  }, []);

  const currentBusiness = businesses.find((b) => b.id === currentBusinessId) ?? null;

  return (
    <BusinessContext.Provider value={{ businesses, currentBusiness, currentBusinessId, setCurrentBusinessId, refreshBusinesses: fetchBusinesses, loading, fetchError }}>
      {children}
    </BusinessContext.Provider>
  );
}
